import React from 'react';
import { Switch } from '@headlessui/react';
import { CurrencyDollarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const PricingInventoryTab = ({ 
  register, 
  errors, 
  watch, 
  setValue, 
  inputClassName,
  setShowWarehousePopup 
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-3">
      <div className="px-6 py-4  from-blue-50 to-indigo-50 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <CurrencyDollarIcon className="h-6 w-6 mr-2 text-[#009efb]" />
          Pricing & Inventory
        </h2>
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Regular Price</label>
            <input
              type="number"
              {...register('regular_price')}
              className={`${inputClassName} ${
                errors.regular_price ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="0.00"
            />
            {errors.regular_price && (
              <p className="mt-1 text-sm text-red-600">{errors.regular_price.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Member Price</label>
            <input
              type="number"
              {...register('member_price')}
              className={`${inputClassName} ${
                errors.memberPrice ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="0.00"
            />
            {errors.memberPrice && (
              <p className="mt-1 text-sm text-red-600">{errors.memberPrice.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Average Cost</label>
            <input
              type="number"
              {...register('average_cost')}
              className={`${inputClassName} ${
                errors.averageCost ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="0.00"
            />
            {errors.averageCost && (
              <p className="mt-1 text-sm text-red-600">{errors.averageCost.message}</p>
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Inventory Item</label>
          <Switch
            checked={watch('is_inventory_item')}
            onChange={(value) => setValue('is_inventory_item', value)}
            className={`${
              watch('is_inventory_item') ? 'bg-[#009efb]' : 'bg-gray-200'
            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2`}
          >
            <span
              className={`${
                watch('is_inventory_item') ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div>

        {/* Inventory Item Conditional Fields */}
        {watch('is_inventory_item') && (
          <div className="space-y-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="text-lg font-medium text-green-900">Inventory Management</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Reorder Point <span class="text-red-500">*</span></label>
                <input
                  type="number"
                  {...register('reorder_point')}
                  className={`${inputClassName} ${
                    errors.reorder_point ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="0"
                />
                {errors.reorder_point && (
                  <p className="mt-1 text-sm text-red-600">{errors.reorder_point.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Reorder Quantity <span class="text-red-500">*</span></label>
                <input
                  type="number"
                  {...register('reorder_quantity')}
                  className={`${inputClassName} ${
                    errors.reorder_quantity ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="1"
                />
                {errors.reorder_quantity && (
                  <p className="mt-1 text-sm text-red-600">{errors.reorder_quantity.message}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Minimum Across Warehouses <span class="text-red-500">*</span></label>
                <input
                  type="number"
                  {...register('minimum_across_warehouses')}
                  className={`${inputClassName} ${
                    errors.minimum_across_warehouses ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="0"
                />
                {errors.minimum_across_warehouses && (
                  <p className="mt-1 text-sm text-red-600">{errors.minimum_across_warehouses.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Minimum Per Warehouse <span class="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    {...register('minimum_per_warehouses')}
                    placeholder="0"
                    className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                      errors.minimum_per_warehouses ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowWarehousePopup(true)}
                    className="px-4 py-3 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors"
                  >
                    Set Individual ROP's
                  </button>
                </div>
                {errors.minimum_per_warehouse && (
                  <div className="flex items-center text-sm text-red-600">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                    {errors.minimum_per_warehouse.message}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingInventoryTab; 